import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, ParamMap, Router } from '@angular/router';
import { BuyService } from '../../buy/buy.service';
import { Model } from '../../app.model';
import { AutorunComponent } from '@myshared/autorun.component';
import { SubscriptionService } from '../../subscription/subscription.service';
import { BlockNumber, BlockOffer, TrunkNumberBlock } from '../../phonenumber/phonenumber.model';
import { Subscription } from '../../subscription/subscription.model';
import { LacList, PhonenumberService } from '../../phonenumber/phonenumber.service';
import { PaymentMethodCode } from '../../buy/buy.model';
import {
  PortformCustomAddress,
  PortNumberBlockDetails,
  PortNumberOffer,
  PortNumbersPortingFormDetails,
  PortNumberUploadForm
} from '../../phonenumber/port-phonenumber.model';
import { BankAccount, Company, Contact } from '../../company/company.model';
import { firstValueFrom, Subscription as RxSubscription } from "rxjs";
import { runInAction } from "mobx";
import { CompanyService } from "../../company/company.service";
import { Customer } from "../../customer/customer.model";
import { type PortOrderDraft, SessionKvPortOrder } from "../../sessionkv/sessionkv-port-order";
import { SessionKvPortOrderService } from "../../sessionkv/sessionkv-port-order.service";
import { AssetService } from 'src/app/asset.service';
import { DraftSaveStatus } from '../../phonenumber/phonenumber-save-draft-card/phonenumber-save-draft-card.component';

type BuyStep = 'select-subscription'
  | 'select-contact'
  | 'check-drafts'
  | 'creating-offer'
  | 'select-type'
  | 'contact-sales'
  | 'port-prepare'
  | 'port-number'
  | 'upload-phonebill-form'
  | 'prepare-portform'
  | 'cancel-provider-connection'
  | 'download-port-form'
  | 'upload-port-form'
  | 'select-port-date'
  | 'upload-company-files'
  | 'select-blocksize'
  | 'invoice-address'
  | 'payment-method'
  | 'customer-invoice-address'
  | 'customer-payment-method'
  | 'select-phonenumber'
  | 'select-loc-ind-phonenumber'
  | 'numbers-unavailable'
  | 'checkout-summary'
  | 'thank-you-partner'
  | 'thank-you'
  | 'select-lac';

export type PhonenumberType = 'new' | 'port' | 'loc-independent';

function validateBuyStep(input: string, orderName: string): BuyStep | boolean {
  if (['select-contact', 'select-subscription', 'check-drafts', 'select-type', 'contact-sales',
    'customer-invoice-address', 'customer-payment-method', 'invoice-address', 'payment-method',
    'upload-company-files', 'port-prepare', 'port-number', 'cancel-provider-connection', 'download-port-form',
    'upload-port-form', 'select-port-date', 'upload-phonebill-form', 'prepare-portform', 'select-lac'].indexOf(input) !== -1) {
    return input as BuyStep;
  }
  if (orderName &&
    ['select-blocksize', 'select-phonenumber', 'select-loc-ind-phonenumber', 'checkout-summary', 'thank-you', 'thank-you-partner', 'numbers-unavailable']
      .indexOf(input) !== -1) {
    return input as BuyStep;
  }

  return false;
}

const ORDER_LINES_WHITELIST_STEPS: BuyStep[] = ['checkout-summary', 'thank-you', 'thank-you-partner'];

@Component({
    selector: 'app-workflow-phonenumbers',
    templateUrl: './workflow-phonenumbers.component.html',
    styleUrls: [],
    standalone: false
})
export class WorkflowPhonenumbersComponent extends AutorunComponent implements OnInit, OnDestroy {

  public step: BuyStep;
  public orderName: string;
  public newNumberPrice: number;
  public surplusNumberPrice: number;
  public subscription: Subscription;
  public portingFormDetails: PortNumbersPortingFormDetails;
  public selectedCustomer: Customer;
  public company: Company;
  public selectedContactId: string;
  public portDate: Date;
  public paymentMethod: PaymentMethodCode;
  public isPC20CSP: boolean;
  public isPartner: boolean;
  public lacList: LacList;
  public selectedLac: string;
  public isBillFormDraftEnabled = true;
  public isPortFormDraftEnabled = true;
  public applianceDrafts: PortOrderDraft[] = [];
  public selectedApplianceId: string;
  public portNumberOffer: PortNumberOffer;
  public blocks: BlockOffer[];
  public trunkNumbers: BlockNumber[];
  public selectedBlockSize: number;
  public selectedDraft: PortOrderDraft;
  public draftStatus: DraftSaveStatus = 'idle';

  private draftRetryCount = 0;
  private phonenumbersType: PhonenumberType;
  private startFromOrder = false;
  private navigation: RxSubscription;

  constructor(private route: ActivatedRoute,
    private router: Router,
    private buyService: BuyService,
    private subscriptionService: SubscriptionService,
    private m: Model,
    private phonenumberService: PhonenumberService,
    private companyService: CompanyService,
    private sessionKvPortOrderService: SessionKvPortOrderService,
    private assetService: AssetService,
  ) {
    super();

    this.orderName = '';
    this.m.buy.resetCurrentOrder();

    this.route.paramMap.subscribe((params) => {
      if (params.has('id')) {
        const id = params.get('id');
        if (id !== '') {
          this.orderName = id;
          this.buyService.refresh(this.orderName);
        }
      }

      if (params.has('subroute')) {
        const sr = params.get('subroute');
        const newStep = validateBuyStep(sr, this.orderName);
        if (newStep !== false) {
          this.setStep(newStep as BuyStep);
          return;
        }
      }
    });
  }

  ngOnInit() {
    this.navigation = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart && event.navigationTrigger === 'popstate') {
        this.handleBack();
      }
    });

    this.route.queryParamMap.subscribe(async (queryParams) => {
      const draftId = queryParams.get('draftId');
      if (draftId && !this.startFromOrder) {
        this.startFromOrder = true;
        await this.startDraft(draftId);
      }
    })

    if (!this.startFromOrder && !this.orderName) {
      this.selectedApplianceId = '';
      this.navigateToStep('select-subscription');
    }

    if (!this.m.account.hasTrunkAccess && !this.m.account.currentUser.companyIsPartner) {
      this.navigateToStep('contact-sales');
      return;
    }

    if (this.step === 'select-phonenumber' && !this.blocks && !this.m.account.currentUser.trunkEnvironment.manualLacSelectionEnabled) {
      this.navigateToStep('select-blocksize', this.orderName, true);
    } else if (this.step === 'select-phonenumber' && this.m.account.currentUser.trunkEnvironment.manualLacSelectionEnabled) {
      this.checkLacNeededAndNavigate();
    }

    if (this.step !== 'select-subscription' && !this.selectedApplianceId && !this.orderName) {
      this.navigateToStep('select-subscription');
    }

    if (this.step === 'select-loc-ind-phonenumber' && !!this.orderName) {
      this.phonenumbersType = 'loc-independent';
      this.onPhoneBlocksizeSelected(1, true);
    }

    // On this initial steps, we will check where we need to go, and load data for lac if needed
    if (this.step === 'select-blocksize' || this.step === 'select-lac') {
      this.checkLacNeededAndNavigate();
    }

    this.autorun(async () => {
      this.isPartner = this.m.account.currentUser.companyIsPartner;
      this.isPC20CSP = this.m.account.currentUser.companyIsPC20Csp;
      // We need to reload subscription for some calculation
      if (!this.subscription && +this.m.buy.currentOrder.aid > 0) {
        // We need to wait for this call, otherwise we get an error on the next check
        const subscription = await firstValueFrom(this.subscriptionService.getSubscriptionDetails(+this.m.buy.currentOrder.aid));
        this.subscription = Subscription.fromJson(subscription);
      }

      // Partner offer
      if (!this.subscription?.is_my && !this.isPC20CSP) {
        // reload company of the customer
        if (!this.company && this.m.buy.currentOrder.name !== '') {
          let company = this.m.buy.currentOrder.invoice_address;
          if (company.bankAccounts.length <= 0) {
            company = await firstValueFrom(this.companyService.getCompany(company.id));
          }
          runInAction(() => this.company = company)
        }
      }
      if (this.m.buy.currentOrder?.name !== '') {
        this.paymentMethod = this.m.buy.currentOrder?.payment_method;
      }
    });

    this.phonenumberService.refresh(true);
    this.companyService.refresh(true);
  }

  ngOnDestroy(): void {
    this.navigation?.unsubscribe();
  }

  /**
   * If someone clicks the browser's back button, we will cancel the workflow.
   *
   * @private
   */
  handleBack() {
    this.cancelWorkflow();
  }

  private setStep(step: BuyStep) {
    this.step = step;
  }

  private navigateToStep(step: BuyStep, orderId?: string, replaceUrl?: boolean) {
    if (this.step === step) return;
    this.draftStatus = 'idle';
    this.draftRetryCount = 0;
    this.router.navigate(['/do', this.m.workflow.name, step, orderId ?? ''], {
      queryParamsHandling: 'preserve',
      replaceUrl
    });
  }

  onBack() {
    window.history.back();
  }

  private async loadSubscriptionData(applianceId: number): Promise<any> {
    return firstValueFrom(this.subscriptionService.getSubscriptionDetails(applianceId));
  }

  private getTrialPhoneNumberRoute(isMy: boolean, isPartner: boolean, isPC20CSP: boolean): string[] {
    if (isMy) {
      return ['/do', 'trial-phonenumber', 'add-phonenumber'];
    }

    const workflowUrl = isPartner
      ? (isPC20CSP ? ['csp', 'trial-phonenumber'] : ['partner', 'trial-phonenumber'])
      : ['trial-phonenumber'];

    return ['/do', ...workflowUrl, 'add-phonenumber'];
  }

  private extractTrunkNumbers(subscriptionData: any): BlockNumber[] {
    return TrunkNumberBlock.fromJson(subscriptionData).trunkNumbers;
  }

  public async onSubscriptionSelected(applianceId) {
    try {
      this.selectedApplianceId = applianceId;

      const subscriptionData = await this.loadSubscriptionData(+applianceId);
      this.subscription = Subscription.fromJson(subscriptionData);
      if (this.subscription.license_type === 'one_trial') {
        const route = this.getTrialPhoneNumberRoute(this.subscription.is_my, this.isPartner, this.isPC20CSP);
        await this.router.navigate(route, { queryParams: { aid: applianceId } });
        return;
      }
      this.paymentMethod = this.subscription.payment_method;
      this.trunkNumbers = this.extractTrunkNumbers(subscriptionData);
      this.navigateToStep('select-type');
    } catch (error) { }
  }

  private async loadDrafts() {
    return await firstValueFrom(this.sessionKvPortOrderService.loadAll());
  }

  private async loadApplianceDrafts() {
      const allDrafts = await this.loadDrafts();
      this.applianceDrafts = allDrafts.filter(draft =>
        draft.portOrder.applianceId === +this.selectedApplianceId
      )

      return this.applianceDrafts.length > 0;
  }


  public async onDraftSelected(draft: PortOrderDraft) {
    this.selectedDraft = draft;

    await this.performDataFromDraft(draft);

    if (draft.portOrder.losingProviderPhoneBill && draft.portOrder.losingProviderPhoneBill.assetKey) {
        this.isPortFormDraftEnabled = false;
        this.navigateToStep('upload-port-form');
      } else {
        this.isBillFormDraftEnabled = false;
        this.navigateToStep('upload-phonebill-form');
    }
  }

  private async loadCustomerData(customerId: string): Promise<void> {
    try {
      const company = await firstValueFrom(this.companyService.getCompany(customerId));
      runInAction(() => {
        this.company = company;
        this.selectedCustomer = new Customer(
          company.id,
          company.name,
          company.street,
          company.street2,
          company.zip,
          company.city,
          company.country,
          [],
          company.email,
          company.electronic_invoice_email,
          company.contacts,
        );
        this.m.customer.customers[0] = this.selectedCustomer;
      });
    } catch (error) { }
  }



  public onContactSelected(contact: Contact) {
    this.selectedContactId = contact.id;
    if (this.isPortWorkflow) {
      this.navigateToStep('customer-payment-method');
    } else {
      this.navigateToStep('upload-company-files');
    }
  }

  async startDraft(draftName: string) {
    this.phonenumbersType = 'port';

    const allDrafts = await this.loadDrafts();
    this.selectedDraft = allDrafts.find(r => r.name === draftName);
    const applianceId = this.selectedDraft.portOrder.applianceId;

    this.applianceDrafts = allDrafts.filter(draft =>
        draft.portOrder.applianceId === +applianceId
      )

    const subscriptionData = await this.loadSubscriptionData(applianceId);
    this.subscription = Subscription.fromJson(subscriptionData);

    await this.loadContact();

    this.portNumberOffer = new PortNumberOffer();
    this.portNumberOffer.appliance_id = applianceId;
    this.selectedApplianceId = applianceId.toString();

    await this.onDraftSelected(this.selectedDraft);
  }


  async onPhoneTypeSelected(type: PhonenumberType) {
    if (!this.canEnterType(type)) {
      this.navigateToStep('contact-sales');
      return;
    }

    await this.loadContact();

    this.phonenumbersType = type;

    if (this.isPortWorkflow) {
      this.startPort();
    } else {
      this.startNew();
    }
  }

  private async loadContact() {
     if (!this.subscription.is_my && !this.isPC20CSP) {
      await this.loadCustomerData(this.subscription.customer_id);
    } else {
      this.company = await firstValueFrom(this.companyService.getMyCompany());
    }

  }

  ////
  // PORTING PHONE NUMBER
  ////

  private async startPort() {
      this.portNumberOffer = new PortNumberOffer();
      this.portNumberOffer.appliance_id = +this.selectedApplianceId;

      const hasDrafts = await this.loadApplianceDrafts();

      if (hasDrafts) {
        this.navigateToStep('check-drafts');
      } else {
        this.navigateToStep('port-prepare');
      }
  }

  public onPortPrepare(): void {
    if (this.subscription.is_my || this.isPC20CSP) {
      this.company = this.m.company.company;
      this.navigateToStep('payment-method');
    } else {
      this.navigateToStep('select-contact');
    }
  }

  public onPortNumbersSelected(formDetails: PortNumbersPortingFormDetails) {
    this.portNumberOffer.number_blocks = formDetails.numbers;
    this.portingFormDetails = formDetails;
    this.navigateToStep('upload-phonebill-form');
  }

  public async onPhoneBillUploaded(form: PortNumberUploadForm) {
    this.portNumberOffer.phonebill_file = form;
    this.navigateToStep('prepare-portform');
  }

  public async onSaveDraft(pos: 'upload_bill' | 'upload_port') {
    this.draftStatus = 'saving';

    const builder = new SessionKvPortOrder().setBasicData({
        applianceId: +this.subscription.appliance_id,
        contactId: +this.selectedContactId || null,
        losingProvider: this.portingFormDetails?.provider || ''
      });

    // Save phone numbers
    if (this.portingFormDetails?.numbers && this.portingFormDetails.numbers.length > 0) {
      const firstNumber = this.portingFormDetails.numbers[0];

      if (firstNumber.block_start && firstNumber.block_end) {
        // Save data for a block number
        builder.setNumberBlock({
          countryPrefix: firstNumber.country_prefix || '',
          areaCode: firstNumber.area_code || '',
          mainNumber: firstNumber.main_number || '',
          blockStart: firstNumber.block_start,
          blockEnd: firstNumber.block_end
        });
      } else {
        // Save data for single numbers
        const phoneNumbers = this.portingFormDetails.numbers.map(num => ({
          countryPrefix: num.country_prefix || '',
          areaCode: num.area_code || '',
          number: num.main_number || ''
        }));

        builder.setSingleNumbers({phoneNumbers});
      }
    }

    // Upload phone bill form to S3 and save the asset key to get this file later
    if (pos === 'upload_port') {
      try {
        const file = this.portNumberOffer.phonebill_file.file;
        const assetData = await firstValueFrom(this.assetService.placeAsset(file.name, 'application/pdf'));
        await firstValueFrom(this.assetService.upload(assetData.uploadUrl, file));
        builder.setFile({assetKey: assetData.assetKey});
      } catch (error) {
        console.error('Error uploading phone bill:', error);
        this.draftRetryCount++;
        this.draftStatus = this.draftRetryCount >= 2 ? 'failed' : 'error';
        return;
      }
    }

    const portOrderProto = builder.getProto();

    // Choose draft, override the existing one if name is existing
    const name = this.selectedDraft?.name ?? undefined;
    this.sessionKvPortOrderService.save({ portOrder: portOrderProto, name })
      .subscribe({
        next: (e) => {
          this.draftStatus = 'saved';
          this.draftRetryCount = 0;

          if (e.value.$case === 'portOrder') {
            this.selectedDraft = {
              name: e.name,
              portOrder: e.value.portOrder
            }
          }
        },
        error: (error) => {
          console.error('Error saving draft:', error);
          this.draftRetryCount++;
          this.draftStatus = this.draftRetryCount >= 2 ? 'failed' : 'error';
        }
      });
  }


  public onCompanyName({customAddress, name}: { customAddress?: PortformCustomAddress, name: string }) {
    if (name) {
      this.portingFormDetails.company_name = name;
      this.portingFormDetails.company_id = +this.company.id;
      this.portNumberOffer.bill_company_name = name;

      if (customAddress) {
        this.portingFormDetails.custom_address = customAddress
      }

      if (this.subscription.trunkEnvironment.tenantCountryCode === 'DE') {
        this.navigateToStep('cancel-provider-connection');
      } else {
        this.portingFormDetails.cancel_connection = false;
        this.navigateToStep('download-port-form');
      }
    } else {
      this.navigateToStep('prepare-portform');
    }
  }

  public onPortFormOptionsSelected(cancelConnection: boolean) {
    this.portingFormDetails.cancel_connection = cancelConnection;
    this.navigateToStep('download-port-form');
  }

  public onPortFormDownloaded(event: any) {
    this.navigateToStep('upload-port-form');
  }

  public onPortFormUploaded(form: PortNumberUploadForm) {
    this.portNumberOffer.porting_form_file = form;
    if (!this.subscription.is_my && !this.isPC20CSP) {
      this.portNumberOffer.partner_id = +this.selectedContactId;
    }
    this.navigateToStep('select-port-date');
  }

  public onPortDateSelected(date: string) {
    this.portNumberOffer.delivery_date = date;

    this.buyService.createNumberBlockPortInOffer(this.portNumberOffer).subscribe(_ => {
      this.orderName = this.m.buy.currentOrder.name;
      this.navigateToStep('checkout-summary', this.orderName);
    });

  }

  ////
  // ORDER NEW NUMBER
  ////

  private startNew() {
    if (this.subscription.is_my || this.isPC20CSP) {
      this.navigateToStep('upload-company-files');
    } else {
      this.navigateToStep('select-contact');
    }
  }

  async onPhoneBlocksizeSelected(size: number, replaceUrlForNavigation?: boolean) {
    this.selectedBlockSize = size;
    this.getPhonenumbersFromBlocksize(replaceUrlForNavigation);
  }

  public onPhonennumberSelected(blockIdsMap: Map<string, boolean>) {
    const blockIds: string[] = [];
    for (const [k, _] of blockIdsMap) {
      blockIds.push(k);
    }

    this.buyService.addPhonenumberToOffer(blockIds).subscribe(r => {
      this.navigateToStep('checkout-summary', this.orderName);
    });
  }

  public onLocIndPhonennumberSelected(blockId: string) {
    const blockIds: string[] = [];
    blockIds.push(blockId);

    this.buyService.addPhonenumberToOffer(blockIds).subscribe(r => {
      this.navigateToStep('checkout-summary', this.orderName);
    });
  }

  public onCustomerInvoiceAddressFilled(company: Company) {
    this.companyService.update(company).subscribe(ok => {
      const bankAccounts = this.company.bankAccounts;
      runInAction(() => {
        this.company = company;
        this.company.bankAccounts = bankAccounts;
      })

      this.navigateToStep('customer-payment-method', this.orderName);
    });
  }

  public onInvoiceAddressFilled(company: Company) {
    this.company = company;
    this.navigateToStep('payment-method', this.orderName);
  }

  public async onCustomerPaymentMethodSelected(details: { bank?: BankAccount, method?: PaymentMethodCode }) {

    if (details?.method === 'sepa') {
      if (!this.isPortWorkflow) {
        details.bank.partner_id = +this.company.id;
      }
      await firstValueFrom(this.companyService.updateBankAccount(details.bank));
      this.company.bankAccounts[0] = details.bank;
    }

    if (this.isPortWorkflow) {
      this.navigateToStep('port-number', undefined, true);
    } else if (this.isLocationIndependentWorkflow) {
      this.onPhoneBlocksizeSelected(1, true);
    } else {
      this.checkLacNeededAndNavigate(true);
    }
  }

  public async onPaymentMethodSelected(details: { bank?: BankAccount, method?: PaymentMethodCode }) {
    if (this.isLocationIndependentWorkflow) {
      this.onPhoneBlocksizeSelected(1, true);
      return;
    }

    if (this.isPortWorkflow) {
      this.navigateToStep('port-number', undefined, true);
    } else {
      this.checkLacNeededAndNavigate(true);
    }
  }

  public onCompanyFilesUploaded(uploadData: { file_name: string, file_data: string }) {
    const data = {
      appliance_id: this.selectedApplianceId,
      partner_id: this.selectedContactId ?? undefined
    }
    this.buyService.createEmptyOffer(data).subscribe(_ => {
      this.orderName = this.m.buy.currentOrder.name;
      this.buyService.addCompanyCommercialRegisterToOffer(uploadData).subscribe(r => {
        if (this.subscription.is_my || this.isPC20CSP) {
          this.navigateToStep('invoice-address', this.orderName);
        } else {
          this.navigateToStep('customer-invoice-address', this.orderName);
        }
      });
    });
  }

  public onLacBack() {
    this.onBack();
  }

  public onLacSelected(lacValue: string) {
    this.selectedLac = lacValue;
    this.navigateToStep('select-blocksize', this.orderName);
  }

  private getPhonenumbersFromBlocksize(replaceUrlForNavigation?: boolean) {
    this.buyService.getPhonenumbersByBlocksize(
      this.orderName,
      this.selectedBlockSize.toString(),
      this.isLocationIndependentWorkflow,
      this.selectedLac,
    ).subscribe({
      next: (r: []) => {
        this.blocks = r.map(b => BlockOffer.fromJson(b));
        this.navigateToStep(this.isLocationIndependentWorkflow ?
          'select-loc-ind-phonenumber' : 'select-phonenumber', this.orderName, replaceUrlForNavigation);
      },
      error: (err) => {
        this.navigateToStep('numbers-unavailable', this.orderName, replaceUrlForNavigation);
      }
    });
  }

  public onPhonenumberUnavailableBack() {
    this.checkLacNeededAndNavigate();
  }

  public onBuy() {
    if (this.m.account.currentUser.company_id !== this.m.buy.currentOrder.invoice_address.id) {
      this.onBuyPartner();
      return;
    }

    this.onBuyCustomer();
  }

  public resetWorkflow() {
    this.m.workflow.resetWorkflow();
  }

  public cancelWorkflow() {
    this.m.workflow.resetWorkflow();
    let url = '/phonenumbers';

    if (this.startFromOrder) {
      url = '/orders';
    }

    window.location.replace(url);
  }

  public get backLabel() {
    return this.isPortWorkflow ? 'app_cancel' : 'app_back';
  }

  public get isPortWorkflow(): boolean {
    return this.phonenumbersType === 'port';
  }

  public get isLocationIndependentWorkflow(): boolean {
    return this.phonenumbersType === 'loc-independent';
  }

  private onBuyPartner() {
    this.buyService.confirmPartnerQuotation().subscribe(_ => {
      this.removeDrafts();
      this.navigateToStep('thank-you-partner', this.orderName);
    });
  }

  private onBuyCustomer() {
    this.buyService.userConfirmOffer().subscribe(_ => {
      this.removeDrafts();
      this.navigateToStep('thank-you', this.orderName);
    });
  }

  private removeDrafts() {
    if (this.selectedDraft) {
      const allFoundDrafts = this.findMultipleDrafts(this.selectedDraft);
      for(const draftName of allFoundDrafts) {
        this.sessionKvPortOrderService.delete(draftName).subscribe();
      }
    }
  }

  private async checkLacNeededAndNavigate(replaceUrl?: boolean) {
    if (this.m.account.currentUser.trunkEnvironment.manualLacSelectionEnabled) {
      this.navigateToStep('select-lac', this.orderName, replaceUrl);
    } else {
      this.navigateToStep('select-blocksize', this.orderName, replaceUrl);
    }
  }

  private canEnterType(type: PhonenumberType) {
    if (type === 'new' && this.canEnterNewNumber) return true;
    if (type === 'port' && this.canEnterPortNumber) return true;
    if (type === 'loc-independent' && this.canEnterLocIndependentNumber) return true;

    return false;
  }

  private get canEnterLocIndependentNumber() {
    return this.subscription.trunkEnvironment.isLocationIndepEnabled;
  }
  private get canEnterNewNumber() {
    return this.subscription.trunkEnvironment.isLocationDepEnabled;
  }
  private get canEnterPortNumber() {
    return this.subscription.trunkEnvironment.isPortingEnabled;
  }

  private async performDataFromDraft(draft: PortOrderDraft) {
    if (!this.phonenumbersType) this.phonenumbersType = 'port';
    if (!this.portNumberOffer) this.portNumberOffer = new PortNumberOffer();
    if (!this.portingFormDetails) this.portingFormDetails = new PortNumbersPortingFormDetails();

    this.portNumberOffer.appliance_id = draft.portOrder.applianceId;

    if (!this.subscription.is_my) {
      this.selectedContactId = draft.portOrder.contactId.toString();
      this.portNumberOffer.partner_id = draft.portOrder.contactId !=0 ? draft.portOrder.contactId : undefined;
    }

    if (draft.portOrder.numberBlocks.$case === 'numberBlock') {
      const numberBlock = new PortNumberBlockDetails();
      numberBlock.area_code = draft.portOrder.numberBlocks.numberBlock.areaCode;
      numberBlock.country_prefix = draft.portOrder.numberBlocks.numberBlock.countryPrefix;
      numberBlock.main_number = draft.portOrder.numberBlocks.numberBlock.mainNumber;
      numberBlock.block_start = draft.portOrder.numberBlocks.numberBlock.blockStart;
      numberBlock.block_end = draft.portOrder.numberBlocks.numberBlock.blockEnd;
      this.portNumberOffer.number_blocks = [];
      this.portNumberOffer.number_blocks[0] = numberBlock;
    }

    if (draft.portOrder.numberBlocks.$case === 'singleNumbers') {
      this.portNumberOffer.number_blocks = draft.portOrder.numberBlocks.singleNumbers.phoneNumbers.map(number => {
        const numberBlock = new PortNumberBlockDetails();
        numberBlock.area_code = number.areaCode;
        numberBlock.country_prefix = number.countryPrefix;
        numberBlock.main_number = number.number;
        numberBlock.block_start = '';
        numberBlock.block_end = '';
        return numberBlock;
      });
    }

    this.portingFormDetails.provider = draft.portOrder.losingProvider;
    this.portingFormDetails.numbers = this.portNumberOffer.number_blocks;

    if (draft.portOrder?.losingProviderPhoneBill && draft.portOrder?.losingProviderPhoneBill?.assetKey) {
      const asset = await firstValueFrom(this.assetService.getAsset(draft.portOrder.losingProviderPhoneBill.assetKey));

      let filename = 'phonebill.pdf';
      if (asset.fileMetaData.meta.$case === 'assetMetaV1') {
        filename = asset.fileMetaData.meta.assetMetaV1.fileName;
      }

      const file = await firstValueFrom(this.assetService.download(asset.downloadUrl, filename));

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const fileResult = reader.result.toString().replace(/^[^,]*,/, '');
        this.portNumberOffer.phonebill_file = {
          file_name: file.name,
          file_body_b64: fileResult,
          file: file,
        };
      };
      reader.readAsDataURL(file);
    }

    return draft;
  }

  public onStartNewPort() {
    this.navigateToStep('port-prepare');
  }

  private findMultipleDrafts(draft: PortOrderDraft) {
    const selectedDraft = draft.portOrder.numberBlocks;
    return this.applianceDrafts.filter(r => {
      const thatDraft = r.portOrder.numberBlocks;
      return JSON.stringify(selectedDraft) === JSON.stringify(thatDraft);
    }).map(r => r.name);
  }
}
